/**
 * Authentication state for the app (§13).
 *
 * Responsibilities kept deliberately narrow: hold the session, refresh it,
 * and tell the router whether there is one. Everything about *what the user
 * can see* belongs to the route guards, because §42 is clear that frontend
 * authorization is never authoritative — a guard here is a UX affordance, and
 * the backend is what actually says no.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { authApi, profilesApi } from '@/api/endpoints';
import { ApiError, toApiError } from '@/api/errors';
import { setAuthBridge, type RefreshOutcome } from '@/api/client';
import {
  accessTokenIsStale,
  clearEverything,
  clearSession,
  getAccessToken,
  loadPersisted,
  persist,
  setAccessToken,
  type PersistedSession,
} from './session';

export type AuthStatus = 'restoring' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  userId: string | null;
  role: PersistedSession['role'] | null;
  isParent: boolean;
  signIn(tokens: { access_token: string; refresh_token: string; expires_in: number }, user: { user_id: string; role: PersistedSession['role'] }): void;
  /**
   * Open a child's profile on this device (§17).
   *
   * No PIN, from a parent's session or a sibling's alike — a shared family
   * phone that needs a parent every time it changes hands gets used once.
   */
  enterChild(studentId: string): Promise<void>;
  /** Back to the parent side, which is the half worth locking. */
  enterParent(pin: string): Promise<void>;
  /**
   * Run one parent-only operation from inside the child's app.
   *
   * Takes the PIN, borrows the parent's session for the length of `work`,
   * and hands it back. See the implementation for why the handing back is
   * the part that needed care.
   */
  borrowParentSession<T>(pin: string, work: () => Promise<T>): Promise<T>;
  /** True while a borrowed session has not made it back to the child yet. */
  owesSessionBack: boolean;
  signOut(): Promise<void>;
}

/**
 * Exported so tests can supply a fixed session.
 *
 * The provider itself restores from storage and refreshes on a timer — both
 * correct in the app and both noise in a component test, which wants to assert
 * what a signed-in child sees, not re-prove the token lifecycle.
 */
export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('restoring');
  const [session, setSession] = useState<PersistedSession | null>(null);
  // Mirrors `pendingReturn` for rendering. The ref is what the retry reads,
  // because it has to be correct inside a callback that was created before
  // the last render.
  const [owesSessionBack, setOwesSessionBack] = useState(false);

  // A single in-flight refresh shared by every caller. Without this, six
  // queries firing on a cold load produce six refresh calls, five of which
  // race and lose — and token rotation turns the losers into sign-outs.
  const refreshInFlight = useRef<Promise<RefreshOutcome> | null>(null);

  /**
   * True while the device is deliberately changing hands.
   *
   * A hand-off revokes the parent's session server side, on purpose. Any
   * request still in flight at that moment gets a 401 — and the ordinary
   * response to a 401 is "refresh, and sign out if that fails", which is
   * exactly the wrong thing here: it tears down the session a few
   * milliseconds before the new one is installed, and the parent lands back
   * on the login screen holding a phone they just handed over.
   *
   * Found by the end-to-end run, not by reasoning: it depends on whether a
   * background refetch happens to overlap the hand-off request, so it passed
   * on one run and failed on the next.
   */
  const switching = useRef(false);

  /**
   * A child we owe a session back to.
   *
   * Set when a borrowed parent session could not be handed back, and cleared
   * when it is. The `online` listener below drains it, so a dropped return is
   * a delay rather than a device left holding a parent credential inside a
   * child's app.
   */
  const pendingReturn = useRef<string | null>(null);

  const applyTokens = useCallback(
    (tokens: { access_token: string; refresh_token: string; expires_in: number }, user: { user_id: string; role: PersistedSession['role'] }) => {
      setAccessToken(tokens.access_token, tokens.expires_in);
      const persisted: PersistedSession = {
        refreshToken: tokens.refresh_token,
        userId: user.user_id,
        role: user.role,
      };
      persist(persisted);
      setSession(persisted);
      setStatus('authenticated');
    },
    [],
  );

  const endSession = useCallback(() => {
    if (switching.current) return;
    clearSession();
    setSession(null);
    setStatus('anonymous');
  }, []);

  /**
   * §42: "Never mix state between children."
   *
   * Every cached query — mastery, profile, recommendations — belongs to whoever
   * the session was for. Swapping identity without emptying the cache shows one
   * child the other's screen for as long as it takes the refetch to land, which
   * is exactly the bug the rule exists to prevent.
   *
   * `keepCache` is the one case where that rule does not apply, and it is
   * narrow on purpose: borrowing the parent's session for a single operation
   * and handing it straight back (see `borrowParentSession`). No child is
   * changing — the same child is on screen before and after — so clearing
   * would throw away that child's own mastery, recommendations and homework
   * thread twice every time their parent looked at a progress report. Parent
   * data lands under its own query keys, so nothing is mixed.
   */
  const switchIdentity = useCallback(
    (
      tokens: { access_token: string; refresh_token: string; expires_in: number },
      user: { user_id: string; role: PersistedSession['role'] },
      options: { keepCache?: boolean } = {},
    ) => {
      clearSession();
      if (!options.keepCache) queryClient.clear();
      applyTokens(tokens, user);
    },
    [applyTokens, queryClient],
  );

  /**
   * Both switches are the same two steps: ask the server for a session as
   * somebody else, then adopt it. The server revokes the old one as part of
   * answering, so there is never a moment where the device holds two live
   * identities.
   */
  const enterChild = useCallback(
    async (studentId: string) => {
      switching.current = true;
      try {
        // Nothing in flight when the old session stops being valid.
        await queryClient.cancelQueries();
        const result = await profilesApi.enterChild(studentId);
        switchIdentity(result.tokens, {
          user_id: result.user.user_id,
          role: 'student',
        });
      } finally {
        switching.current = false;
      }
    },
    [queryClient, switchIdentity],
  );

  const enterParent = useCallback(
    async (pin: string) => {
      switching.current = true;
      try {
        await queryClient.cancelQueries();
        const result = await profilesApi.enterParent(pin);
        switchIdentity(result.tokens, {
          user_id: result.user.user_id,
          role: result.user.role,
        });
      } finally {
        switching.current = false;
      }
    },
    [queryClient, switchIdentity],
  );

  /**
   * Hand the session back to the child, and keep trying until it lands.
   *
   * Two immediate attempts, because the overwhelmingly likely failure is one
   * dropped request; then the child id is parked and the `online` listener
   * below picks it up. The borrow's own error is not swallowed -- a failed
   * return is reported separately, so a parent who saw their report does not
   * also see a scary error, and the app still gets itself back.
   */
  const returnToChild = useCallback(
    async (studentId: string) => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const back = await profilesApi.enterChild(studentId);
          switchIdentity(
            back.tokens,
            { user_id: back.user.user_id, role: 'student' },
            { keepCache: true },
          );
          pendingReturn.current = null;
          setOwesSessionBack(false);
          return;
        } catch (cause) {
          const error = toApiError(cause);
          // A rejection means the swap back is never going to work -- the
          // link is gone, or the child was removed while we were away. Retrying
          // that forever is worse than stopping.
          if (error.isAuthFailure || error.code === 'RESOURCE_NOT_FOUND') throw cause;
        }
      }
      pendingReturn.current = studentId;
      setOwesSessionBack(true);
    },
    [switchIdentity],
  );

  /**
   * Borrow the parent's session for one operation, then hand it straight back.
   *
   * There is one template now. The child's app is the app, and the things that
   * belong to a parent -- a progress report, adding a sibling, exporting or
   * erasing a child's data -- are buttons inside it that ask for the PIN. The
   * production numbers are what killed the old shape: twenty-four profile
   * switches produced six progress views, so the ceremony was four times the
   * thing it was for, and four of five accounts have exactly one child.
   *
   * The backend still authorizes those endpoints by role, so this cannot be
   * done in the client alone: the session really is swapped to the parent's
   * for the duration of `work` and swapped back after. Three properties make
   * that safe to do on a nine-year-old's phone on mobile data:
   *
   * *The cache survives.* `keepCache` -- see `switchIdentity`. The same child
   * is on screen before and after, and clearing twice per progress view would
   * make their own app re-fetch everything every time their parent looked.
   *
   * *The return always happens.* It is in a `finally`, it is retried, and a
   * return that still fails is remembered and retried when the network comes
   * back. Being stranded in a parent session inside what looks like the
   * child's app is the one outcome worse than a 403, and with the parent area
   * gone there would be nothing on screen to show it.
   *
   * *Nothing can end the session while it is in flight.* `switching` is held
   * across the whole borrow, so a stray 401 from a request that was already
   * on its way out does not tear down a session that is mid-swap.
   */
  const borrowParentSession = useCallback(
    async <T,>(pin: string, work: () => Promise<T>): Promise<T> => {
      const stored = loadPersisted();
      if (!stored) throw new ApiError({ code: 'AUTHENTICATION_REQUIRED', status: 401 });

      // Who to go back to. A parent borrowing from their own session returns
      // to themselves, which is a no-op worth allowing rather than special
      // casing -- first run is a parent with no child to return to.
      const returnTo = stored.role === 'student' ? stored.userId : null;

      switching.current = true;
      try {
        await queryClient.cancelQueries();
        const asParent = await profilesApi.enterParent(pin);
        switchIdentity(
          asParent.tokens,
          { user_id: asParent.user.user_id, role: asParent.user.role },
          { keepCache: true },
        );

        try {
          return await work();
        } finally {
          if (returnTo) await returnToChild(returnTo);
        }
      } finally {
        switching.current = false;
      }
    },
    [queryClient, switchIdentity, returnToChild],
  );


  const refresh = useCallback(async (): Promise<RefreshOutcome> => {
    // See `switching`: a 401 during a deliberate identity swap is expected,
    // and must not end the session that is about to be replaced.
    // `rejected`, not `unreachable`: during a hand-off the server really has
    // revoked the old session, and the stray request deserves to hear that
    // rather than a connection error it can retry. Nothing is torn down --
    // `endSession` returns early while `switching` is set, which is the whole
    // reason that flag exists.
    if (switching.current) return 'rejected';
    if (refreshInFlight.current) return refreshInFlight.current;

    const attempt = (async (): Promise<RefreshOutcome> => {
      const stored = loadPersisted();
      if (!stored) {
        endSession();
        return 'rejected';
      }
      try {
        const tokens = await authApi.refresh(stored.refreshToken);
        applyTokens(tokens, { user_id: stored.userId, role: stored.role });
        return 'ok';
      } catch (err) {
        // §13 says a failed refresh ends the session and must not retry, and
        // this used to read that as "any failure at all". On the connections
        // this product is built for, that was the single biggest reason a
        // child ever saw the sign-in screen: one dropped request on mobile
        // data and a session with twenty-nine days left on it was thrown
        // away locally, while the server still considered it live.
        //
        // So only the server saying no ends it. A refresh token is not
        // invalidated by a train going into a tunnel.
        const error = toApiError(err);
        if (error.isAuthFailure) {
          endSession();
          return 'rejected';
        }
        // The session is kept. The caller decides what to show, the retry
        // paths below try the same token again, and §13's no-retry rule is
        // honoured because this function still does not loop -- it reports
        // and returns.
        return 'unreachable';
      } finally {
        refreshInFlight.current = null;
      }
    })();

    refreshInFlight.current = attempt;
    return attempt;
  }, [applyTokens, endSession]);

  // Hand the client its bridge once, so it can refresh on 401 without
  // importing React state.
  useEffect(() => {
    setAuthBridge({
      getAccessToken,
      refresh,
      onAuthFailure: endSession,
    });
  }, [refresh, endSession]);

  // Restore on boot: a persisted refresh token becomes a live session, or the
  // child lands on the welcome screen.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const stored = loadPersisted();
      if (!stored) {
        if (!cancelled) setStatus('anonymous');
        return;
      }
      const outcome = await refresh();
      if (cancelled) return;
      if (outcome === 'rejected') {
        setStatus('anonymous');
        return;
      }
      // `unreachable` keeps the stored session and lets the app open. The
      // screens show their own "no connection, try again", which is true,
      // instead of a sign-in screen, which is not: the session is still
      // valid and the retries below pick it up when the network returns.
      //
      // The identity has to be adopted from storage as well as the status,
      // and not only so the greeting has a name: `role` is what the parent
      // route guard reads, so a status without it sends a child booting on a
      // dead connection to the wrong side of the app.
      setSession(stored);
      setStatus('authenticated');
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally once: re-running this on every `refresh` identity change
    // would re-authenticate on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh proactively while the app is open, so a child mid-lesson never
  // hits a 401 they can see.
  useEffect(() => {
    if (status !== 'authenticated') return;
    const timer = setInterval(() => {
      if (accessTokenIsStale()) void refresh();
    }, 60_000);
    return () => clearInterval(timer);
  }, [status, refresh]);

  // The other half of not signing out on a blip: once the connection is
  // back, take it. Without this the child is left holding a session that is
  // valid but unproven until they happen to trigger another request.
  useEffect(() => {
    if (status !== 'authenticated') return;
    const retry = () => {
      // A session we still owe a child comes first: until it is handed back
      // the device is holding a parent credential inside a child's app.
      const owed = pendingReturn.current;
      if (owed) {
        void returnToChild(owed).catch(() => {
          // Still not reachable, or refused for good. `returnToChild` has
          // already recorded which, and the next reconnect tries again.
        });
        return;
      }
      if (accessTokenIsStale()) void refresh();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') retry();
    };
    window.addEventListener('online', retry);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [status, refresh, returnToChild]);

  const signOut = useCallback(async () => {
    try {
      await authApi.signOut();
    } catch {
      // Revoking server-side is best-effort; the local session goes either way.
    }
    // An explicit sign-out means leaving the account, not just ending a
    // session. The device is clean.
    clearEverything();
    queryClient.clear();
    setSession(null);
    setStatus('anonymous');
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      userId: session?.userId ?? null,
      role: session?.role ?? null,
      isParent: session?.role === 'parent',
      signIn: applyTokens,
      enterChild,
      enterParent,
      borrowParentSession,
      owesSessionBack,
      signOut,
    }),
    [
      status,
      session,
      applyTokens,
      enterChild,
      enterParent,
      borrowParentSession,
      owesSessionBack,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
